import React, { Fragment } from 'react';
import NewPatientAgeInput from './common/NewPatientAgeInput';
import { DatePicker } from 'antd';
import { DAYS, MONTHS, YEARS } from './common/constants/add-patient';
import { getErrorComp } from './PatientPhoneAutoSuggest';

const AgeInputBlock: React.FC<{
    isAgeInputDisabled: boolean;
    value: {
        id: YEARS | MONTHS | DAYS;
        label: string;
        value: number;
    } | null;
    onAgeSelect: (
        option: {
            id: YEARS | MONTHS | DAYS;
            label: string;
            value: number;
        } | null,
    ) => void;
    onDobChange: (dateValue: moment.Moment | null) => void;
    momentDate?: moment.Moment | null;
    disabledDate?: (date: moment.Moment) => boolean;
    isDobError: boolean;
    isAgeDOBError: boolean;
    isModal: boolean;
    dob?:
        | {
              dd?: string | undefined;
              mm?: string | undefined;
              yyyy?: string | undefined;
          }
        | undefined;
    onDDDobChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    onMMDobChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    onYYYYDobChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
    today?: moment.Moment;
}> = ({
    isAgeInputDisabled,
    value,
    onAgeSelect,
    onDobChange,
    momentDate,
    disabledDate,
    isDobError,
    isAgeDOBError,
    isModal,
    dob,
    onDDDobChange,
    onMMDobChange,
    onYYYYDobChange,
    today,
}) => {
    return !isModal && today ? (
        <Fragment>
            <div className="w-15p">
                <div className="space-y-4">
                    <div className="flex justify-between">
                        <p className="text-neutral-3 text-14 font-700">Age</p>
                        {isAgeInputDisabled && (
                            <span className="text-12 self-end mb-2 text-status-error select-none">
                                (can not be edited)
                            </span>
                        )}
                    </div>
                    <NewPatientAgeInput
                        value={value}
                        disabled={isAgeInputDisabled}
                        onSelect={onAgeSelect}
                    />
                </div>
            </div>

            <p className="mt-32 font-700 text-neutral-5">OR</p>

            <div className="w-328">
                <div className="space-y-4">
                    <div className="flex justify-between">
                        <p className="text-neutral-3 text-14 font-700">Date of Birth</p>
                        {isAgeInputDisabled && (
                            <span className="text-12 self-end mb-2 text-status-error select-none">
                                (can not be edited)
                            </span>
                        )}
                    </div>
                    <div className="flex items-center space-x-8">
                        <div className="add-pt-dob-select w-33p">
                            <select
                                required
                                value={dob?.dd || '0'}
                                onChange={onDDDobChange}
                                className="w-full text-neutral-black font-600 focus:outline-none bg-transparent"
                                disabled={isAgeInputDisabled}
                            >
                                <option value="">DD</option>
                                {[...Array(31).keys()].map((_, i) => (
                                    <option key={i + 1} value={i + 1}>
                                        {i + 1}
                                    </option>
                                ))}
                            </select>
                            <div className="mimic-box"></div>
                        </div>
                        <div className="add-pt-dob-select w-33p">
                            <select
                                required
                                value={dob?.mm || '0'}
                                onChange={onMMDobChange}
                                className="w-full text-neutral-black font-600 focus:outline-none bg-transparent"
                                disabled={isAgeInputDisabled}
                            >
                                <option value="">MM</option>
                                <option value="1">Jan</option>
                                <option value="2">Feb</option>
                                <option value="3">Mar</option>
                                <option value="4">Apr</option>
                                <option value="5">May</option>
                                <option value="6">Jun</option>
                                <option value="7">Jul</option>
                                <option value="8">Aug</option>
                                <option value="9">Sep</option>
                                <option value="10">Oct</option>
                                <option value="11">Nov</option>
                                <option value="12">Dec</option>
                            </select>
                            <div className="mimic-box"></div>
                        </div>
                        <div className="add-pt-dob-select w-33p">
                            <select
                                required
                                value={dob?.yyyy || '0'}
                                onChange={onYYYYDobChange}
                                className="w-full text-neutral-black font-600 focus:outline-none bg-transparent"
                                disabled={isAgeInputDisabled}
                            >
                                <option value="">YYYY</option>
                                {[...Array(111).keys()].map((_, i) => (
                                    <option key={today.year() - i} value={today.year() - i}>
                                        {today.year() - i}
                                    </option>
                                ))}
                            </select>
                            <div className="mimic-box"></div>
                        </div>
                    </div>
                </div>
                {isDobError && getErrorComp('Invalid Date of Birth')}
                {isAgeDOBError && getErrorComp('Age or date of birth is required')}
            </div>
        </Fragment>
    ) : (
        <div className="w-full">
            <div className="w-full flex gap-16">
                <NewPatientAgeInput
                    value={value}
                    onSelect={onAgeSelect}
                    isModal={isModal}
                    disabled={isAgeInputDisabled}
                />
                <div className="bg-darwin-neutral-50 w-50p">
                    <DatePicker
                        className="bg-darwin-neutral-50 add-new-pt-date-picker w-full h-full"
                        onChange={onDobChange}
                        allowClear={false}
                        value={momentDate}
                        bordered
                        format={'DD/MM/YYYY'}
                        placeholder="Enter Date of Birth"
                        suffixIcon={
                            <svg
                                width="13"
                                height="15"
                                viewBox="0 0 13 15"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                            >
                                <path
                                    d="M10.875 2.5C11.832 2.5 12.625 3.29297 12.625 4.25V13C12.625 13.9844 11.832 14.75 10.875 14.75H2.125C1.14062 14.75 0.375 13.9844 0.375 13V4.25C0.375 3.29297 1.14062 2.5 2.125 2.5H3.21875V1.40625C3.21875 1.05078 3.49219 0.75 3.875 0.75C4.23047 0.75 4.53125 1.05078 4.53125 1.40625V2.5H8.46875V1.40625C8.46875 1.05078 8.79688 0.75 9.125 0.75C9.48047 0.75 9.78125 1.05078 9.78125 1.40625V2.5H10.875ZM10.875 13.4375C11.0938 13.4375 11.3125 13.2461 11.3125 13V6H1.6875V13C1.6875 13.2461 1.87891 13.4375 2.125 13.4375H10.875Z"
                                    fill="#3E445C"
                                />
                            </svg>
                        }
                        disabledDate={disabledDate}
                        style={{ borderStyle: 'none' }}
                        disabled={isAgeInputDisabled}
                    />
                </div>
            </div>
            {isDobError && <div className="mt-8">{getErrorComp('Invalid Date of Birth')}</div>}
            {isAgeDOBError && (
                <div className="mt-8">{getErrorComp('Age or date of birth is required')}</div>
            )}
        </div>
    );
};

export default AgeInputBlock;
